import React from 'react';
import { useTranslation } from 'react-i18next';
import { SvgPlayFilled } from '../../../icons';
import { Asset, SeriesAsset } from '../../../queries/ats/titles/types/asset';
import { SvodSource } from '../../../queries/ats/titles/types/sources';
import { getSeasons } from '../../../queries/ats/titles/utils/array-utils';
import { routes } from '../../../routes/route-utils';
import { useAuth } from '../../../utils/auth-utils';
import { useDetails } from '../details-context';
import { FocusedAsset } from '../use-focused-asset';
import { appendSequenceNumbers } from '../utils';
import { CallToAction } from './call-to-action-button';
import { ContinueWatchingButton } from './continue-watching-button';
import { SvodServiceLink } from './svod-service-link';
import { SvodGuestUser } from './svod-guest-user';

interface SvodCallToActionProps {
  asset: Asset;
  focusedAsset: FocusedAsset;
  source: SvodSource;
}

export function SvodCallToAction({ asset, focusedAsset, source }: SvodCallToActionProps) {
  const { t } = useTranslation();
  const { isGuest } = useAuth();

  if (isGuest) {
    return <SvodGuestUser asset={asset} focusedAsset={focusedAsset} source={source} />;
  }

  if (asset.type === 'series') {
    return <SvodSeriesCallToAction asset={asset} focusedAsset={focusedAsset} source={source} />;
  }

  if (!source.isSubscribed) {
    return <SvodServiceLink source={source} />;
  }

  if (source.bookmark !== undefined) {
    return <ContinueWatchingButton focusedAsset={focusedAsset} source={source} bookmark={source.bookmark} />;
  }

  const url = routes.play({
    platformTitleId: source.platformTitleId,
    providerIdentifier: source.providerIdentifier,
    masterId: focusedAsset.masterId,
  });

  return (
    <CallToAction
      text={appendSequenceNumbers(t('details:watch-now'), asset)}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgPlayFilled />}
      to={url}
    />
  );
}

interface SvodSeriesCallToActionProps {
  asset: SeriesAsset;
  focusedAsset: FocusedAsset;
  source: SvodSource;
}

export function SvodSeriesCallToAction({ asset, focusedAsset, source }: SvodSeriesCallToActionProps) {
  const { selectedContext } = useDetails('SvodSeriesCallToAction');

  const episodes = selectedContext.episodes(asset);
  const seasons = getSeasons(episodes);

  const { t } = useTranslation();

  if (!source.isSubscribed) {
    return <SvodServiceLink source={source} subtitle={t('common:seasons', { count: seasons.length })} />;
  }

  if (source.bookmark !== undefined) {
    return <ContinueWatchingButton focusedAsset={focusedAsset} source={source} bookmark={source.bookmark} />;
  }

  const url = routes.play({
    platformTitleId: source.platformTitleId,
    providerIdentifier: source.providerIdentifier,
    masterId: focusedAsset.masterId,
  });

  return (
    <CallToAction
      text={appendSequenceNumbers(t('details:watch-now'), focusedAsset)}
      image={{
        src: source.image,
        alt: source.name,
      }}
      icon={<SvgPlayFilled />}
      to={url}
    />
  );
}
